import React, { useState, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { getScheduleSelection } from "../scheduleSelection"
import {
	addNurse,
	setOptimizeData,
	updateSchedule,
	setIsEditing,
	transferPatientsBetweenNursesAndChairs
} from "../../../../features/schedule/scheduleSlice"
import {
	updateTomorrowSchedule,
	addTomorrowNurse,
	setTomorrowData,
	transferPatientsBetweenNursesAndChairsTomorrow
} from "../../../../features/schedule/tomorrowSlice"
import {
	updateSandboxSchedule,
	transferPatientsBetweenNursesAndChairsSandbox
} from "../../../../features/schedule/sandboxSlice"
import { useAuth0 } from "@auth0/auth0-react"
import ConfirmationDialog from "../ConfirmationDialog"
import store from "../../../../store"
import {
	getDateFormatted,
	getFirstOpenDateToday,
	getFirstOpenDateTomorrow,
	timeStringToMinutes,
	getClinicTimes
} from "../../../../utils/generalUtils"
import PatientReassignmentConfirmation from "../PatientReassignmentConfirmation"
import AddNurseButton from "./AddNurseButton"
import PickNursePopup from "./PickNursePopup"
import { getScheduleFromMongo } from "../../../../utils/mongoUtils"
import { useSchedulePatch } from "../../../../hooks/useSchedulePatch"
import { startTimeAllCases, endTimeAllCases } from "../../../../utils/realTimeUtils"
import useLogging from "../../../../hooks/useLogging"
import useFetchSettings from "../../../../hooks/useFetchSettings"
import { getAssignmentsForNurseAddition } from "../../../../hooks/usePatientReassignments"

/*
type Reassignment = {
  newNurseId: string
  patientId: string
}
*/

const AddNursePopup = ({ setNotification, onClose }) => {
	const { user } = useAuth0()

	const [selectedNurse, setSelectedNurse] = useState(null)

	const sandbox = useSelector(state => state.sandbox.isSandboxMode)
	const isTomorrowMode = useSelector(state => state.tomorrow.isTomorrowMode)
	const schedulePatch = useSchedulePatch({ isTomorrowMode, sandbox })
	const { settingsData: settings } = useFetchSettings()

	const testEnvironment = user.org_id === "org_lkg3WMhi3Q2l92iT"
	const centerOpen = settings?.centerOpen
	const centerClosedDates = settings?.center_closed_dates || []

	// Calculate the appropriate date and open time
	const targetDate = isTomorrowMode
		? getFirstOpenDateTomorrow(centerOpen, testEnvironment, centerClosedDates)
		: getFirstOpenDateToday(centerOpen, testEnvironment, centerClosedDates)

	const { openTime: openTimeToday } = getClinicTimes(settings, targetDate)

	const date = getDateFormatted(targetDate)

	const getSelectedNurseFromSettings = useCallback(() => {
		const settingsNurse = settings.nurses.find(nurse => nurse.nurseId === selectedNurse.nurseId)
		if (!settingsNurse) {
			throw new Error(`Nurse with id ${selectedNurse.nurseId} not found in settings data`)
		}
		return settingsNurse
	}, [settings, selectedNurse])

	const logging = useLogging()

	const scheduleSelection = getScheduleSelection(isTomorrowMode, sandbox)

	const [reassignments, setReassignments] = useState([])

	// openPopup: "none" | "pickNurse" | "confirmation" | "reassignment"
	const [openPopup, setOpenPopup] = useState("none")

	const [isSaving, setIsSaving] = useState(false)

	const calculateTimelineBoundaries = selectedNurse => {
		const suggestedBreakStart = timeStringToMinutes(settings?.break_start_time)
		const suggestedBreakEnd = timeStringToMinutes(settings?.break_end_time)

		const baseTime = Math.max(selectedNurse.startTime, openTimeToday)

		return selectedNurse.endTime >= suggestedBreakEnd &&
			selectedNurse.startTime < suggestedBreakStart
			? [suggestedBreakStart, suggestedBreakEnd]
			: [baseTime + 60, baseTime + 240]
	}

	// Regular close handler for escape/click-outside
	const handleClose = () => {
		if (!isSaving) {
			// Only reset editing state if we're not in the middle of saving
			setSelectedNurse(null)
			setReassignments([])
			dispatch(setIsEditing(false))
		}
		logging.info("AddNursePopup - Popup closed", {
			component: "AddNursePopup",
			function: "handleClose",
			selectedNurse: selectedNurse
		})
		setOpenPopup("none")
		// onClose()
	}

	const handlePopupOpen = () => {
		logging.info("AddNursePopup - handlePopupOpen", {
			component: "AddNursePopup",
			function: "handlePopupOpen"
		})
		setOpenPopup("pickNurse")
	}

	const dispatch = useDispatch()
	const { getAccessTokenSilently } = useAuth0()

	const calculateLunchBreak = selectedNurse => {
		const currentState =
			store.getState()[isTomorrowMode ? "tomorrow" : sandbox ? "sandbox" : "schedule"]

		// Step 1: Check if nurse is eligible for a lunch break
		if (selectedNurse.endTime - selectedNurse.startTime <= 300) {
			return { lunchBreakStart: null, lunchBreakEnd: null } // No lunch break required
		}

		// Step 2: Prepare data for analysis
		const [timelineStart, timelineEnd] = calculateTimelineBoundaries(selectedNurse)

		const timelineLength = timelineEnd - timelineStart
		const acuityTimeline = Array(timelineLength).fill(0)

		// Map patient schedules to the timeline
		currentState.patientSchedule.forEach(patient => {
			if (patient.assignedNurse === selectedNurse.nurseId) {
				// Add acuity to the timeline for each relevant minute
				for (
					let minute = Math.max(
						timelineStart,
						startTimeAllCases(patient, sandbox, isTomorrowMode)
					);
					minute <
					Math.min(timelineEnd, endTimeAllCases(patient, sandbox, isTomorrowMode));
					minute++
				) {
					acuityTimeline[minute - timelineStart] += patient.acuity
				}
			}
		})

		// Step 3: Find the optimal 30-minute span with the lowest average acuity
		let optimalStart = null
		let minAverageAcuity = Infinity

		for (let start = 0; start < timelineLength; start += 10) {
			// Last 30-minute span starts at 210
			const spanAcuity = acuityTimeline
				.slice(start, start + settings.break_duration)
				.reduce((sum, acuity) => sum + acuity, 0)

			const averageAcuity = spanAcuity / settings.break_duration

			if (averageAcuity < minAverageAcuity) {
				minAverageAcuity = averageAcuity
				optimalStart = start
			}
		}

		return {
			lunchBreakStart: optimalStart + timelineStart,
			lunchBreakEnd: optimalStart + settings.break_duration + timelineStart
		}
	}

	const updateNurseLunchBreak = (nurseId, lunchBreakStart, lunchBreakEnd) => {
		const currentState =
			store.getState()[isTomorrowMode ? "tomorrow" : sandbox ? "sandbox" : "schedule"]

		const nurse = currentState.nurseSchedule.find(nurse => nurse.nurseId === nurseId)

		const updatedNurseData = {
			...nurse,
			scheduledLunchBreakStart: lunchBreakStart,
			scheduledLunchBreakEnd: lunchBreakEnd
		}

		const updatedNurseSchedule = currentState.nurseSchedule.map(nurse =>
			nurse.nurseId === nurseId ? updatedNurseData : nurse
		)

		if (sandbox) {
			dispatch(updateSandboxSchedule({ nurseSchedule: updatedNurseSchedule }))
		} else if (isTomorrowMode) {
			dispatch(updateTomorrowSchedule({ nurseSchedule: updatedNurseSchedule }))
		} else {
			dispatch(updateSchedule({ nurseSchedule: updatedNurseSchedule }))
		}

		return updatedNurseData
	}

	const createNewNurse = async () => {
		const { lunchBreakStart, lunchBreakEnd } = calculateLunchBreak(selectedNurse)

		// Find the nurse in settings data to get their role
		const settingsNurse = getSelectedNurseFromSettings()

		const newNurseData = {
			id: Math.floor(Math.random() * 1000000), // Generate a random integer id
			nurseId: settingsNurse.nurseId,
			nurseName: settingsNurse.nurseName,
			nurseEmail: settingsNurse.nurseEmail,
			scheduledEndTime: selectedNurse.endTime,
			scheduledStartTime: selectedNurse.startTime,
			scheduledLunchBreakStart: lunchBreakStart,
			scheduledLunchBreakEnd: lunchBreakEnd,
			nurseRole: settingsNurse?.role || "RN",
			actualStartTime: null,
			actualLunchBreakStart: null,
			actualLunchBreakEnd: null,
			actualEndTime: null
		}

		return newNurseData
	}

	const handleNurseAddition = async () => {
		setOpenPopup("none")
		let updatedNurseSchedule
		const newNurseData = await createNewNurse()
		logging.info("AddNursePopup - Cofirmed nurse addition", {
			component: "AddNursePopup",
			function: "handleNurseAddition",
			newNurseData: newNurseData
		})
		setIsSaving(true)

		let token

		try {
			token = await getAccessTokenSilently({
				audience: process.env.REACT_APP_API_IDENTIFIER
			})
		} catch (error) {
			logging.error("Error fetching merged schedule:", {
				error: error.message,
				stack: error.stack
			})
		}

		const currentState =
			store.getState()[isTomorrowMode ? "tomorrow" : sandbox ? "sandbox" : "schedule"]

		const updateNurseSchedule = (schedule, newNurseData) => {
			const existingNurseIndex = schedule.findIndex(
				nurse => nurse.nurseId === newNurseData.nurseId
			)

			if (existingNurseIndex !== -1) {
				// Replace existing nurse data
				return schedule.map((nurse, index) =>
					index === existingNurseIndex ? newNurseData : nurse
				)
			} else {
				// Add new nurse
				return [...schedule, newNurseData]
			}
		}

		if (!sandbox) {
			const mergedData = await getScheduleFromMongo(
				token,
				date,
				dispatch,
				currentState,
				isTomorrowMode,
				sandbox,
				setTomorrowData,
				setOptimizeData,
				logging
			)

			updatedNurseSchedule = updateNurseSchedule(mergedData.nurseSchedule, newNurseData)
		} else {
			updatedNurseSchedule = updateNurseSchedule(currentState.nurseSchedule, newNurseData)
		}
		const reassignments = getAssignmentsForNurseAddition(
			selectedNurse,
			store.getState()[isTomorrowMode ? "tomorrow" : sandbox ? "sandbox" : "schedule"],
			settings,
			sandbox,
			isTomorrowMode
		)
		setReassignments(reassignments)

		if (sandbox) {
			logging.info("AddNursePopup - Updating sandbox schedule", {
				component: "AddNursePopup",
				function: "handleNurseAddition",
				updatedNurseSchedule: updatedNurseSchedule
			})
			dispatch(updateSandboxSchedule({ nurseSchedule: updatedNurseSchedule }))
		} else if (isTomorrowMode) {
			logging.info("AddNursePopup - Updating tomorrow schedule", {
				component: "AddNursePopup",
				function: "handleNurseAddition",
				updatedNurseSchedule: updatedNurseSchedule
			})
			dispatch(addTomorrowNurse(newNurseData))
			dispatch(updateTomorrowSchedule({ nurseSchedule: updatedNurseSchedule }))
		} else {
			logging.info("AddNursePopup - Updating today's schedule", {
				component: "AddNursePopup",
				function: "handleNurseAddition",
				updatedNurseSchedule: updatedNurseSchedule
			})
			dispatch(addNurse(newNurseData))
			dispatch(updateSchedule({ nurseSchedule: updatedNurseSchedule }))
		}

		if (reassignments && reassignments.length > 0) {
			logging.info("AddNursePopup - There is a suggested reassignment", {
				component: "AddNursePopup",
				function: "handleNurseAddition",
				selectedNurse: selectedNurse,
				reassignments: reassignments
			})
			setOpenPopup("reassignment")
		} else {
			logging.info("AddNursePopup - No suggested reassignment", {
				component: "AddNursePopup",
				function: "handleNurseAddition",
				selectedNurse: selectedNurse
			})
			setOpenPopup("none")
			handleTransitionDecline()
		}
	}

	const handleTransitionAccept = async () => {
		if (!selectedNurse) return
		logging.info("AddNursePopup - Suggestions accepted", {
			component: "AddNursePopup",
			function: "handleTransitionAccept",
			selectedNurse: selectedNurse,
			reassignments: reassignments
		})

		setOpenPopup("none")

		// First dispatch the transfers
		if (sandbox) {
			dispatch(transferPatientsBetweenNursesAndChairsSandbox(reassignments))
		} else if (isTomorrowMode) {
			dispatch(transferPatientsBetweenNursesAndChairsTomorrow(reassignments))
		} else {
			dispatch(transferPatientsBetweenNursesAndChairs(reassignments))
		}

		const { lunchBreakStart, lunchBreakEnd } = calculateLunchBreak(selectedNurse)

		updateNurseLunchBreak(selectedNurse.nurseId, lunchBreakStart, lunchBreakEnd)

		try {
			await getAccessTokenSilently({
				audience: process.env.REACT_APP_API_IDENTIFIER
			})

			await schedulePatch.mutateAsync({ date, setNotification })
		} catch (error) {
			logging.error("Error updating schedule in MongoDB:", {
				error: error.message,
				stack: error.stack
			})
		}

		// Send notification
		setNotification({
			visible: true,
			message: "Nurse Added with Reassigned Patients",
			success: true
		})
		logging.info("AddNursePopup - Added nurse with suggested patients", {
			component: "AddNursePopup",
			function: "handleTransitionAccept",
			selectedNurse: selectedNurse
		})
		setTimeout(() => setNotification({ visible: false, message: "" }), 3000)
		setReassignments([])
		dispatch(setIsEditing(false))
		setIsSaving(false)
	}

	const handleTransitionDecline = async () => {
		setOpenPopup("none")
		logging.info("AddNursePopup - Suggestions declined", {
			component: "AddNursePopup",
			function: "handleTransitionDecline",
			selectedNurse: selectedNurse,
			reassignments: reassignments
		})

		try {
			await getAccessTokenSilently({
				audience: process.env.REACT_APP_API_IDENTIFIER
			})

			await schedulePatch.mutateAsync({ date, setNotification })
		} catch (error) {
			logging.error("Error updating schedule in MongoDB:", {
				error: error.message,
				stack: error.stack
			})
		}

		// Notify that the nurse has been added successfully
		setNotification({
			visible: true,
			message: "Nurse Added Successfully",
			success: true
		})

		setTimeout(() => {
			setNotification(prev => ({ ...prev, visible: false }))
		}, 2000)

		logging.info("AddNursePopup - Added nurse without patients", {
			component: "AddNursePopup",
			function: "handleTransitionAccept",
			selectedNurse: selectedNurse
		})

		// Close the transition dialog
		setReassignments([])
		setSelectedNurse("")
		dispatch(setIsEditing(false))
		setIsSaving(false)
	}

	return (
		<>
			<AddNurseButton onClick={handlePopupOpen} />
			<PickNursePopup
				isOpen={openPopup === "pickNurse"}
				onClose={() => {
					if (openPopup !== "pickNurse") {
						return
					}
					handleClose()
				}}
				onNurseSelected={newlySelectedNurse => {
					setSelectedNurse(newlySelectedNurse)
					setOpenPopup("confirmation")
					logging.info("AddNursePopup - Nurse selected", {
						component: "AddNursePopup",
						function: "onNurseSelected",
						newlySelectedNurse: newlySelectedNurse
					})
				}}
				scheduleSelection={scheduleSelection}
				sandbox={sandbox}
				logging={logging}
			/>
			<ConfirmationDialog
				title="Add New Nurse"
				message={`Are you sure you want to add ${
					selectedNurse ? getSelectedNurseFromSettings().nurseName : "Unknown Nurse"
				} to the schedule?`}
				isOpen={openPopup === "confirmation"}
				onConfirm={() => {
					handleNurseAddition()
				}}
				onCancel={() => {
					if (openPopup !== "confirmation") {
						return
					}
					handleClose()
				}}
			/>
			<PatientReassignmentConfirmation
				title={"Recommended Assignments"}
				message={"Below are suggested assignments for the new nurse"}
				reassignments={reassignments}
				isOpen={openPopup === "reassignment"}
				onAccept={handleTransitionAccept}
				onDecline={handleTransitionDecline}
				scheduleSelection={scheduleSelection}
			/>
		</>
	)
}

export default AddNursePopup
